import Hammer from 'hammerjs';
import { BehaviorSubject, interval, Subject, Observable } from 'rxjs';
import { map, subscribeOn, filter, takeWhile, switchMap, mergeMap, tap } from 'rxjs/operators';
import store from '../store/index';
import { utilsService } from './utils.service';

export interface TouchInput extends HammerInput {
  offsetX?: number;
  offsetY?: number;
}

export enum GameState {
  'unload',
  'loading',
  'started',
  'prestart',
  'playing',
  'done',
  'end',
}

interface confirmData {
  isOpen: boolean;
  data: any;
}

class PlaycanvasService {
  label = 'Playcanvas';
  app = {} as pc.Application;
  hammer: HammerManager = {} as HammerManager;
  hammerData$: Subject<TouchInput> = new Subject();
  timer$ = new BehaviorSubject(0);
  assets$: BehaviorSubject<any> = new BehaviorSubject({});
  PRELOAD_MODULES = [
    {
      moduleName: 'Ammo',
      glueUrl: 'assets/lib/ammo/ammo.wasm.js',
      wasmUrl: 'assets/lib/ammo/ammo.wasm.wasm',
      fallbackUrl: 'assets/lib/ammo/ammo.js',
      preload: true,
    },
  ];

  confirm$ = new BehaviorSubject({ isOpen: false, data: {} } as confirmData);

  /** 实例化pc并存到vuex中 */
  init(canvas: HTMLElement) {
    const app = new pc.Application(canvas, {
      elementInput: new pc.ElementInput(canvas),
      mouse: new pc.Mouse(canvas),
      touch: new pc.TouchDevice(canvas),
      keyboard: new pc.Keyboard(window),
      graphicsDeviceOptions: {
        antialias: true,
        // alpha: false, // 背景透明
        // preserveDrawingBuffer: false,
        preferWebGl2: true,
      },
    });

    // 最高像素比，精度
    app.graphicsDevice.maxPixelRatio = 3;

    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(pc.RESOLUTION_AUTO);

    // 官方bug暂时未解决，临时取消重力。目前重力会递增
    let systems: any = app.systems;
    // if (systems.rigidbody) systems.rigidbody.gravity.set(0, 0, 0);

    window.addEventListener('resize', () => app.resizeCanvas());

    // this.app = app;
    store.dispatch('update', { key: 'pc', data: app });
    this.loader(app);

    console.log('app', app);
    this.initHammer(app.graphicsDevice.canvas);
    return app;
  }

  /** 添加hammer，提供多点触摸支持 */
  initHammer(target: HTMLElement) {
    const options: HammerOptions = {
      // playcanvas 官方配置，适用于hammer1.x版本，目前我们使用hammer2.0
      // prevent_default: true,
      // drag_max_touches: 2,
      // transform_min_scale: 0.08,
      // transform_min_rotation: 180,
      // transform_always_block: true,
      // hold: false,
      // release: false,
      // swipe: false,
      // tap: false,
    };

    let isPinch = false;
    let tmpX = 0;
    let tmpY = 0;

    this.hammer = new Hammer(target, options);
    this.hammer.get('pinch').set({ enable: true });

    this.hammer.on('panstart', e => {
      this.hammerData$.next(e);
      tmpX = e.deltaX;
      tmpY = e.deltaY;
    });
    this.hammer.on('panmove', e => {
      if (isPinch) return;
      let offsetX = e.deltaX - tmpX;
      let offsetY = e.deltaY - tmpY;
      // console.log({ offsetX, offsetY });
      this.hammerData$.next(Object.assign({}, e, { offsetX, offsetY }));
      tmpX = e.deltaX;
      tmpY = e.deltaY;
    });
    this.hammer.on('panend', e => this.hammerData$.next(e));

    this.hammer.on('pinchstart', e => {
      isPinch = true;
      this.hammerData$.next(e);
    });
    this.hammer.on('pinchmove', e => this.hammerData$.next(e));
    this.hammer.on('pinchend', e => {
      isPinch = false;
      this.hammerData$.next(e);
    });

    console.log('hammer', this.hammer);
  }

  /** 添加键盘方向键支持 */
  getKeyboardMotion(app: pc.Application) {
    let x = 0;
    let y = 0;
    let z = 0;

    if (app.keyboard.isPressed(pc.KEY_UP)) y = 1;
    if (app.keyboard.isPressed(pc.KEY_DOWN)) y = -1;
    if (app.keyboard.isPressed(pc.KEY_LEFT)) x = -1;
    if (app.keyboard.isPressed(pc.KEY_RIGHT)) x = 1;

    if (app.keyboard.isPressed(pc.KEY_A)) z = 1;
    if (app.keyboard.isPressed(pc.KEY_D)) z = -1;
    return { x, y, z };
  }

  /** 添加模型 playcanvas json model */
  modelJsonLoader(app: pc.Application, url: string, name: string, type: string = `model`) {
    console.log(`modelJsonLoader`, url);

    const sub = new Subject();
    app.assets.loadFromUrl(url, type, (err, asset) => {
      console.log('loadFromUrl', { err, asset });
      if (!asset) return;
      sub.next(asset);
      this.assets$.next({ ...this.assets$.value, [name]: asset });
    });

    return sub;
  }

  /** 加载素材 */
  loader(app: pc.Application) {
    // const app = this.app;
    // this.app.assets.loadFromUrl()

    // const a5 = new pc.Asset('a5', 'cubemap');

    // a5.preload = true;
    // app.assets.add(a5);

    app.preload(() => {
      console.log('preload');
    });

    const setProgress = (e: any) => console.log('setProgress', e);
    const hideSplash = (e: any) => console.log('hideSplash', e);

    app.on('preload:end', () => {
      console.log('preload:end');
      app.off('preload:progress');
    });
    app.on('preload:progress', setProgress);
    app.on('start', hideSplash);
  }

  /** 计算滑动距离 */
  countTouchDistance() {}

  /** 创建模型体，测试用 */
  createCube(num: any) {
    const { qx, qy, qz } = num;
    let list: pc.Entity[] = [];

    const getBox = () => {
      const box = new pc.Entity('cube');
      const type = 'box';
      box.addComponent('model', { type });
      return box;
    };

    for (let z = 0; z < qz; z++) {
      for (let x = 0; x < qx; x++) {
        for (let y = 0; y < qy; y++) {
          let box = getBox();
          box.setLocalPosition(x, y, z);
          // console.log(x, y, z);
          list.push(box);
        }
      }
    }

    return list;
  }

  /** 计时器1 */
  timer(startTime: Date, intervalTime = 99) {
    return interval(intervalTime).pipe(
      map(res => {
        let t = new Date();
        let timer = t.getTime() - startTime.getTime();
        return timer;
      }),
    );
  }

  /** 计时器2 */
  timer2(app: pc.Application, start$: Observable<number>, dt$: Observable<number>) {
    let t = 0;
    return start$.pipe(
      filter(res => res < 2),
      tap(res => (app.timeScale = res)),
      filter(res => !!res),
      switchMap(() => dt$),
      map(dt => {
        t += Math.floor(dt * 1000);
        return t;
      }),
    );
  }

  /** skybox */
  createSkybox(app: pc.Application) {
    const sub = new Subject();
    let url = 'skybox/32621136/Helipad.dds';
    let url2 = 'stars/33184583/stars.dds';
    app.assets.loadFromUrl(url, 'cubemap', (err, asset) => {
      console.log({ err, asset });
      if (!asset) return;
      sub.next(asset);
    });
    return sub;
  }

  /** 时间卡点加密 */
  crypto(v: string) {
    const a = btoa(v);
    const arr = Array.prototype.map.call(a, (s: string) => s.charCodeAt(0));
    return JSON.stringify(arr.reverse());

    /**
     * 解密方法
     * atob(JSON.parse(字符串).reverse().map(i => String.fromCharCode(i)).join(''))
     */
  }

  /** 加载pc模块 */
  loadModules(modules: any[], urlPrefix: string, doneCallback: Function) {
    // check for wasm module support
    function wasmSupported() {
      try {
        if (typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function') {
          const module = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
          if (module instanceof WebAssembly.Module) return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
        }
      } catch (e) {}
      return false;
    }

    // load a script
    function loadScriptAsync(url: string, doneCallback: Function) {
      let tag = document.createElement('script');
      tag.onload = function() {
        doneCallback();
      };
      tag.onerror = function() {
        throw new Error('failed to load ' + url);
      };
      tag.async = true;
      tag.src = url;
      document.head.appendChild(tag);
    }

    // load and initialize a wasm module
    function loadWasmModuleAsync(moduleName: string, jsUrl: string, binaryUrl: string, doneCallback: Function) {
      if (window[moduleName as any]) {
        doneCallback();
        console.log(moduleName, window[moduleName as any]);
        return;
      }

      loadScriptAsync('assets/lib/playcanvas.min.js', () => {});
      loadScriptAsync(jsUrl, function() {
        let lib: any = window[moduleName as any];
        // console.log({ lib });
        window[`${moduleName}Lib` as any] = lib;
        lib({
          locateFile: function() {
            return binaryUrl;
          },
        }).then(function(instance: any) {
          window[moduleName as any] = instance;
          // console.log({ instance });
          doneCallback();
        });
      });
    }

    if (typeof modules === 'undefined' || modules.length === 0) {
      // caller may depend on callback behaviour being async
      setTimeout(doneCallback);
    } else {
      let asyncCounter = modules.length;
      let asyncCallback = function() {
        asyncCounter--;
        if (asyncCounter === 0) {
          doneCallback();
        }
      };

      let wasm = wasmSupported();
      modules.forEach(function(m) {
        if (!m.hasOwnProperty('preload') || m.preload) {
          if (wasm) {
            loadWasmModuleAsync(m.moduleName, urlPrefix + m.glueUrl, urlPrefix + m.wasmUrl, asyncCallback);
          } else {
            if (!m.fallbackUrl) {
              throw new Error('wasm not supported and no fallback supplied for module ' + m.moduleName);
            }
            loadWasmModuleAsync(m.moduleName, urlPrefix + m.fallbackUrl, '', asyncCallback);
          }
        } else {
          asyncCallback();
        }
      });
    }
  }

  /** 移除pc和ammo，解决pc销毁后无法正常整合ammo的问题 */
  destroy(app: pc.Application) {
    if (app && app.destroy) app.destroy();
    utilsService.removeJSFile('assets/lib/ammo/ammo.js');
    utilsService.removeJSFile('assets/lib/ammo/ammo.wasm.js');
    utilsService.removeJSFile('assets/lib/playcanvas.min.js');
    let libs = ['Ammo', 'AmmoLib', 'pc'];
    libs.map(name => (window[name] = undefined));
  }

  gameProgress(allcheck: any) {
    if (!allcheck) return 0;
    // 游戏进度 1,2,3,4,5,6
    console.log('allcheck', allcheck);
    const current = allcheck.findIndex(shut => {
      return shut && typeof shut.clearancetime == `number` && shut.clearancetime === 0;
    });
    return current === -1 ? 6 : current + 1;
  }
}

/** 单例对象函数 */
const service = (() => {
  // 隐藏的Class的构造函数
  // function TodayCilpService() {}

  // 未初始化的单例对象
  let service: PlaycanvasService;

  return {
    // 创建/获取单例对象的函数
    getInstance: () => {
      if (!service) {
        service = new PlaycanvasService();
      }
      return service;
    },
  };
})();

/** 导出服务名称 */
export const pcService = service.getInstance();
